import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ApiService } from '../../js/ApiService';
import useClient from '../util/useClient/useClient';
import useEnvSite from '../util/useEnvSite/useEnvSite';
import ErrorSummary from '../util/ErrorSummary/ErrorSummary';
import SafeHtmlError from '../util/SafeHtmlError/SafeHtmlError';

import { passWordValidation } from '../../js/utils';
import InputField from '../util/InputField/InputField';
import RegisterReCaptcha from '../ReCaptcha/RegisterReCaptcha/RegisterReCaptcha';

function ChangePassword() {
  const {
    register,
    errors,
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValid }
  } = useForm({ mode: 'onChange' });
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const client = useClient();
  const envSite = useEnvSite();
  const [password, setPassword] = useState('');
  const [passFocused, setpassFocused] = useState(false);
  const location = useLocation();
  const [reCaptchaToken, setreCaptchaToken] = useState(null);

  useEffect(() => {
    const handleToken = ev => {
      setreCaptchaToken(ev.token);
      setMessage('');
    };
    document.addEventListener('rTokenRdy', handleToken, false);
    return () => {
      document.removeEventListener('rTokenRdy', handleToken, false);
    };
  }, []);

  const params = new URLSearchParams(location.search);

  useEffect(() => {
    document.title = `Update Password | ${client.startsWith('finl') ? 'Finish Line' : 'JD Sports'}`;
  }, [client]);

  const onSubmit = async data => {
    if (!reCaptchaToken) {
      setMessage('You need to complete reCaptcha');
      return;
    }
    setreCaptchaToken(null);
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      try {
        window.grecaptcha.enterprise.reset();
      } catch (e) {
        console.error(e);
      }
    }
    try {
      const res = await ApiService.changePassword(
        {
          newPassword: data.newPassword,
          oldPassword: data.oldPassword,
          recaptchaToken: reCaptchaToken
        },
        provideMobileRedirectValue(params)
      );
      const msg = await res.text();
      setMessage(msg);
      if (res.status === 200) {
        setIsSuccess(true);
      }
    } catch {
      setMessage('An error occurred');
    }
  };

  const handlePassChange = e => {
    setPassword(e.target.value);
  };

  const provideMobileRedirectValue = params => {
    if (params.get('mobileRedirect') != null) {
      return params.get('mobileRedirect');
    }
    return null;
  };

  const showErrorSummary = () => {
    setpassFocused(true);
  };

  const hideErrorSummary = () => {
    setpassFocused(false);
  };

  return (
    <div className={isSuccess ? 'body' : 'body bg-white'}>
      <div className="bg-white d-flex flex-column align-items-center justify-content-center">
        <h1 className="h1 mb-4 text-center" style={{ marginTop: '28px' }}>
          CHANGE PASSWORD
        </h1>
      </div>
      <div className="bg-white pb-3">
        {!isSuccess && (
          <div className="form">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-3 text-left">
                <InputField
                  name="oldPassword"
                  type="password"
                  className="form-input"
                  maxLength="25"
                  placeholder="Old Password"
                  aria-label="Old Password"
                  ref={register({
                    required: {
                      value: true,
                      message: 'This field is required'
                    },
                    maxLength: {
                      value: 25,
                      message: 'The password is maximum 25 characters'
                    }
                  })}
                />
                {errors.oldPassword && (
                  <div className="form-text text-danger" role="alert">
                    {errors.oldPassword.message}
                  </div>
                )}
              </div>

              <div className="mb-3 text-left">
                <InputField
                  name="newPassword"
                  type="password"
                  className="form-input"
                  maxLength="25"
                  placeholder="New Password"
                  aria-label="New Password"
                  ref={register({ validate: passWordValidation })}
                  onChange={handlePassChange}
                  onFocus={showErrorSummary}
                  onBlur={hideErrorSummary}
                />
                {errors.newPassword && (
                  <div className="form-text text-danger" role="alert">
                    {errors.newPassword.message}
                  </div>
                )}
              </div>

              <ErrorSummary value={password} show={passFocused} />

              <div className="mb-3 text-left">
                <InputField
                  name="confirmPassword"
                  type="password"
                  className="form-input"
                  maxLength="25"
                  placeholder="Confirm Password"
                  aria-label="Confirm Password"
                  ref={register({
                    required: 'You must confirm your new password',
                    validate: {
                      equalsNewPassword: value =>
                        value === getValues('newPassword') || 'You must confirm your new password'
                    }
                  })}
                />
                {errors.confirmPassword && (
                  <div className="form-text text-danger" role="alert">
                    {errors.confirmPassword.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <RegisterReCaptcha action="CHANGE_PASSWORD" />
              </div>

              <div className="text-center my-4">
                <input
                  disabled={isSubmitting || !isValid}
                  type="submit"
                  className="btn btn-primary btn-block"
                  value="Submit"
                />
              </div>
            </form>
            <SafeHtmlError message={message} className="text-danger text-center" />
          </div>
        )}
        {isSuccess && (
          <div className="text-center pt-3">
            <p>{message || 'Your password has been changed'}</p>
          </div>
        )}
      </div>
      {isSuccess && (
        <div>
          <hr className="fl-hr"></hr>
          <div className="formFooter text-center">
            <a
              href={
                envSite.redirectURI
                  ? envSite.redirectURI
                  : `https://${envSite.env}.${envSite.site}.com/myaccount/profile`
              }
              className="btn btn-link"
            >
              Go back to {client === 'finl-web' ? 'FinishLine' : 'JDSports'}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChangePassword;
